import React, {useContext, useEffect, useRef} from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import bryntumContext from "../context/bryntumContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import {
  canAddNewUser, copyTextToClipboard,
  showConfirmationDialog
} from "../../other/utils/utils.jsx";
import {ScheduleUserConfig} from "../../other/constants/ScheduleUserConfig.jsx";
import {MessageDialog} from "@bryntum/schedulerpro";
import { callOnLoadOnColumns, canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import {EDITOR_KEYS} from "../../other/constants/EditorKeys.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import UserContactTab from "../../bryntum-lib/editors/userEditor/UserContactTab.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import { SERVER_BASE_URL } from "../../env.jsx";

const EDITOR_TABS = {
  [WIDGET_KEYS.contactColumn]: UserContactTab.type
}

const onDeleteUser = ({usersGrid, user, deactivateRoleId, selectedBoard}) => {
  const recordToRemove = usersGrid.selectedRecord;
  if (recordToRemove) {
    if (user.id !== recordToRemove.userId) {
      if (recordToRemove.id === selectedBoard.record.owner) {
        MessageDialog.alert({
          title: "Cannot Delete User",
          message: "You cannot delete or deactivate board owner!",
        });
        return;
      }
      showConfirmationDialog({
        title: `Delete or Deactivate User`,
        message: `<div>Are you sure you want to delete the following user, including historical data?<br/>This action cannot be undone. Deactivating users preserves historical data.<br/><br/><b>${recordToRemove.name}</b></div>`,
        cancelBtnText: "Delete",
        confirmBtnText: {
          cls: "order-1 b-raised",
          color: "b-blue",
          text: "Deactivate",
        },
        isCloseByHeader: true,
      }).then((isDeactivateUser) => {
        if (isDeactivateUser) {
          recordToRemove.roles = deactivateRoleId;
        } else {
          usersGrid.store.remove(recordToRemove);
        }
      });
    } else {
      MessageDialog.alert({
        title: "Cannot Delete User",
        message: "You cannot delete your own user!",
      });
    }
  }
}

const onFinalizeEmailCellEdit = ({ value, record: currentRecord, grid }, user) => {
  if (value) {
    if (grid.store.find((record) => record.id !== currentRecord.id && record.email?.toLocaleLowerCase() === value.toLocaleLowerCase())) {
      return "The email is already in use!";
    }

    if (currentRecord.userId === user.id) {
      return "You cannot change your email address.";
    }
  }

  return true;
}

const onExpanderToggleHandler = (e, userGrid) => {
  const { record } = e.source.cellInfo;

  if (!record._isLastExpandedState) userGrid.features.rowExpander.expand(record);
  else userGrid.features.rowExpander.collapse(record);

  record._isLastExpandedState = !record._isLastExpandedState;
}

const UserPage = () => {
  const userGridRef = useRef();
  const { setRefs, isRefsInit } = useRefsInit([userGridRef]);
  const { user, project, stateProvider, editorHandler, selectedBoard } = useContext(bryntumContext);
  useEnableViewTracking(userGridRef, VIEW_TYPES.users, isRefsInit);

  const onGridCellClick = debounce(({ record, column, event }) => {

    const { id, type } = event.target.dataset;

    if (column.id === "name" && event.shiftKey) {
      window.open(`${SERVER_BASE_URL}/superuser-page/?email=${record.email}`, "_blank");
      return;
    }

    if (column.id === id && type === 'iconType') {
      copyTextToClipboard(record[id], `Copied ${id} to clipboard`);
    }
    if (canOpenEditor(column, record) && event.detail === 1 && column.type === WIDGET_KEYS.userRoleColumn && !canAddNewUser(user)) {
      editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
        entityRecord: record,
        column,
        event
      }, { by: "recordType", useExisting: true })
    }
  }, 300)

  useEffect(() => {
    if (project && stateProvider) {
      const userGridInstance =  userGridRef.current.instance;
      const canAddNewUserCheck = canAddNewUser(user);
      const emailColumn = userGridInstance.columns.getById("email");

      userGridInstance.readOnly = canAddNewUserCheck;
      userGridInstance.disabled = canAddNewUserCheck;
      // Do not use store directly on this grid as applying filtering, sorters, groupers will affect store
      // hence if the store in being used in anywhere else for instance assigned board users in resource editor may not get all records or might break
      // also don't use project.boardUsersStore.query() as this has another issue which is if from user editor user record is added then the grid behind won't display data until reload
      userGridInstance.store = project.boardUsersStore.chain();

      callOnLoadOnColumns(userGridInstance.columns, project, { editorHandler, user });

      emailColumn.finalizeCellEdit = (e) => onFinalizeEmailCellEdit(e, user);

      userGridInstance.onExpanderToggleHandler = (e) => onExpanderToggleHandler(e, userGridInstance);
      userGridInstance.features.cellMenu.processItems = processCellMenuItems({ grid: userGridInstance, project, editorHandler })

      const deactivateRoleId = project.swivuAppRoles.find((role) => !role.grants.length).id;
      userGridInstance.features.cellMenu.items.removeRow.onItem = () => {
        onDeleteUser({ usersGrid: userGridInstance, user, deactivateRoleId, selectedBoard });
      };

    }
  }, [project, user, stateProvider, editorHandler]);

  return (
    <>
      <div className="main-grid-view main-view-clients main-view-user flex flex-1">
        <BryntumGrid
          stateId
          ref={setRefs(userGridRef)}
          {...ScheduleUserConfig}
          onCellClick={onGridCellClick}
          onCellDblClick={(e) => {
            const { record, column } = e;
            if (canOpenEditor(column, record)) {
              editorHandler.openEditor(EDITOR_KEYS.userEditor, record, {
                user: record,
                selectTabByTypeOnOpen: EDITOR_TABS[column.type] || null
              });
            }

            if (column.id === "email") {
              if (record.userId === user.id && column.id === "email") {
                MessageDialog.alert({
                  title: "Cannot Edit Email",
                  message: "You cannot edit your own email!",
                });
              } else {
                MessageDialog.alert({ title: "Note", message: "Swivu User email addresses have to be a Gmail address or affiliated with a Google Workspace." })
                  .then(() => {
                    userGridRef.current.instance.startEditing({
                      record: record,
                      field: column.field
                    });
                });
              }
            }
          }}
        />
      </div>
    </>
  );
};

export default UserPage;
